import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Card, Button, Space, Typography, Alert, Row, Col,
  Descriptions, Tag, Spin, Divider, message,
} from 'antd'
import { QrcodeOutlined, ToolOutlined, CloseCircleOutlined } from '@ant-design/icons'
import { useScanQr, useCreateOrdineLavoro } from '@/hooks/useTireService'
import type { QrScanResult } from '@/types/tireService'
import QrScanner from '@/components/QrScanner'

const { Title, Text } = Typography

const STATO_COLORS: Record<string, string> = {
  CAMBIO: 'blue',
  SOLO_DEPOSITO: 'default',
  CAMBIO_E_DEPOSITO: 'purple',
}

export default function TireCheckinPage() {
  const [scannerActive, setScannerActive] = useState(false)
  const [scanned, setScanned] = useState(false)
  const [result, setResult] = useState<QrScanResult | null>(null)
  const navigate = useNavigate()

  const scanQr = useScanQr()
  const createOrdine = useCreateOrdineLavoro()

  const handleScan = async (codice: string) => {
    if (scanned) return
    setScanned(true)
    setScannerActive(false)
    try {
      const res = await scanQr.mutateAsync(codice.trim())
      setResult(res)
    } catch {
      message.error('QR non trovato o già utilizzato')
      setScanned(false)
    }
  }

  const handleCreaOrdine = async () => {
    if (!result) return
    try {
      await createOrdine.mutateAsync({ prenotazione_id: result.prenotazione.id })
      navigate('/tire-service')
    } catch {
      message.error('Errore nella creazione dell\'ordine di lavoro')
    }
  }

  const handleReset = () => {
    setResult(null)
    setScanned(false)
    setScannerActive(false)
  }

  return (
    <div style={{ maxWidth: 700, margin: '0 auto', padding: 24 }}>
      <Title level={3}>
        <QrcodeOutlined style={{ marginRight: 8 }} />
        Check-in Cambio Pneumatici
      </Title>

      {/* Scanner */}
      {!result && (
        <Card style={{ marginBottom: 24, textAlign: 'center' }}>
          {!scannerActive ? (
            <Button
              type="primary"
              size="large"
              icon={<QrcodeOutlined />}
              onClick={() => { setScannerActive(true); setScanned(false) }}
              style={{ height: 56, fontSize: 18, minWidth: 220 }}
            >
              Scansiona QR Cliente
            </Button>
          ) : (
            <>
              <Button
                danger
                size="large"
                icon={<CloseCircleOutlined />}
                onClick={() => setScannerActive(false)}
                style={{ marginBottom: 16 }}
              >
                Annulla scansione
              </Button>
            </>
          )}

          <QrScanner active={scannerActive} onScan={handleScan} />

          {scanQr.isPending && (
            <div style={{ marginTop: 24 }}>
              <Spin size="large" tip="Verifica QR in corso…" />
            </div>
          )}
        </Card>
      )}

      {/* Risultato */}
      {result && (
        <Card
          title={
            <Space>
              <Text strong>{result.customer.nome} {result.customer.cognome}</Text>
              <Tag color={STATO_COLORS[result.prenotazione.tipo_servizio]}>
                {result.prenotazione.tipo_servizio.replace(/_/g, ' ')}
              </Tag>
            </Space>
          }
          extra={
            <Space>
              <Button icon={<QrcodeOutlined />} onClick={handleReset}>
                Nuova scansione
              </Button>
              <Button
                type="primary"
                icon={<ToolOutlined />}
                onClick={handleCreaOrdine}
                loading={createOrdine.isPending}
              >
                Apri Ordine di Lavoro
              </Button>
            </Space>
          }
        >
          <Row gutter={24}>
            <Col xs={24} sm={12}>
              <Descriptions title="Cliente" size="small" column={1}>
                <Descriptions.Item label="Nome">{result.customer.nome} {result.customer.cognome}</Descriptions.Item>
                <Descriptions.Item label="Telefono">{result.customer.telefono || '—'}</Descriptions.Item>
                <Descriptions.Item label="Email">{result.customer.email || '—'}</Descriptions.Item>
              </Descriptions>
            </Col>
            <Col xs={24} sm={12}>
              <Descriptions title="Veicolo" size="small" column={1}>
                <Descriptions.Item label="Targa"><strong>{result.vehicle.targa}</strong></Descriptions.Item>
                <Descriptions.Item label="Modello">{result.vehicle.marca} {result.vehicle.modello}</Descriptions.Item>
              </Descriptions>
            </Col>
          </Row>

          {result.set_in_deposito && (
            <>
              <Divider />
              <Alert
                type="info"
                showIcon
                message={
                  <Space>
                    <Text>Set in deposito trovato:</Text>
                    <Text strong>{result.set_in_deposito.misura} {result.set_in_deposito.stagione}</Text>
                    {result.set_in_deposito.posizione && (
                      <Tag color="blue">Scaffale: {result.set_in_deposito.posizione}</Tag>
                    )}
                  </Space>
                }
              />
            </>
          )}

          {!result.set_in_deposito && (
            <>
              <Divider />
              <Alert type="warning" showIcon message="Nessun set in deposito trovato per questo veicolo." />
            </>
          )}
        </Card>
      )}
    </div>
  )
}
